<template>
  <div class="connections">
    <section class="card">
      <div class="card-head">
        <h3>{{ brand.agencyName }}</h3>
        <p>This location is using the account you opened this app from.</p>
      </div>
      <div v-if="ghlLoading" class="empty">Checking {{ brand.agencyName }}…</div>
      <div v-else-if="ghlConnected" class="status-row ok">
        <span class="dot" />
        <div>
          <strong>Signed in</strong>
          <p v-if="ghlStatus?.locationId">Location {{ ghlStatus.locationId }}</p>
          <p v-else>Company {{ ghlStatus?.companyId || 'connected' }}</p>
        </div>
      </div>
      <div v-else class="status-row bad">
        <span class="dot" />
        <div>
          <strong>Not signed in</strong>
          <p>Open this app from inside {{ brand.agencyName }} so it can see this location.</p>
        </div>
      </div>
    </section>

    <section class="card">
      <div class="card-head">
        <h3>ServiceM8</h3>
        <p>Jobs and clients sync through this login. Reconnect if the link expires.</p>
      </div>
      <ConnectionStatus ref="connectionStatus" @status-changed="handleStatusChange" />
      <ConnectButton v-if="!isConnected" @connected="handleServiceM8Connected" />
    </section>
  </div>
</template>

<script>
import ConnectionStatus from './ConnectionStatus.vue'
import ConnectButton from './ConnectButton.vue'
import { brandMixin } from '../brandMixin'

export default {
  name: 'ConnectionsTab',
  mixins: [brandMixin],
  components: {
    ConnectionStatus,
    ConnectButton,
  },
  data() {
    return {
      isConnected: false,
      ghlConnected: false,
      ghlLoading: true,
      ghlStatus: null,
    }
  },
  async mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const servicem8Connected = urlParams.get('servicem8_connected');
    if (servicem8Connected === 'true') {
      await this.checkConnectionStatus();
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    await this.checkGHLStatus();
    await this.checkConnectionStatus();
  },
  methods: {
    handleStatusChange(status) {
      if (typeof status === 'object' && status !== null) {
        this.isConnected = status.connected;
      } else {
        this.isConnected = status;
      }
    },
    async handleServiceM8Connected() {
      await this.checkConnectionStatus();
      if (this.$refs.connectionStatus) {
        await this.$refs.connectionStatus.checkStatus();
      }
    },
    async checkGHLStatus() {
      this.ghlLoading = true;
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/api/ghl/status', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.ghlConnected = data.connected;
          this.ghlStatus = data;
        } else {
          this.ghlConnected = false;
        }
      } catch (error) {
        console.error('Error checking GHL status:', error);
        this.ghlConnected = false;
      } finally {
        this.ghlLoading = false;
      }
    },
    async checkConnectionStatus() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/status', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.isConnected = data.connected;
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        const finish = (value) => {
          window.removeEventListener('message', onMessage);
          resolve(value);
        };
        const timer = setTimeout(() => finish(''), 2500);
        const onMessage = ({ data }) => {
          if (data?.message === 'REQUEST_USER_DATA_RESPONSE') {
            clearTimeout(timer);
            finish(data.payload);
          }
        };
        window.addEventListener('message', onMessage);
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
      });
    },
  },
}
</script>

<style scoped>
.connections {
  display: grid;
  gap: 16px;
}

.card {
  background: #fff;
  border: 1px solid #e6eef6;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 18px 20px 20px;
}

.card-head {
  margin-bottom: 14px;
}

.card-head h3 {
  margin: 0;
  font-size: 17px;
  color: #0f172a;
}

.card-head p,
.empty,
.status-row p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
}

.empty {
  padding: 12px 0;
}

.status-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  border-radius: 12px;
  background: #f8fbff;
}

.status-row strong {
  display: block;
  color: #0f172a;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  background: #94a3b8;
  flex-shrink: 0;
}

.status-row.ok {
  background: #ecfdf5;
}

.status-row.ok .dot {
  background: #10b981;
}

.status-row.bad {
  background: #fef2f2;
}

.status-row.bad .dot {
  background: #ef4444;
}
</style>
